import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { 
  companyService, 
  Company, 
  CompanyFormData, 
  CompanyListParams 
} from '@/services/company/companyService';

export interface CompanyState {
  companies: Company[];
  company: Company | null;
  companiesLoading: boolean;
  isLoading: boolean;
  error: string | null;
  pagination: {
    currentPage: number;
    totalPages: number;
    totalItems: number;
    itemsPerPage: number;
  } | null;
}

const initialState: CompanyState = {
  companies: [],
  company: null,
  companiesLoading: false,
  isLoading: false,
  error: null,
  pagination: null,
};

export const fetchCompanies = createAsyncThunk(
  'company/fetchCompanies',
  async (params: CompanyListParams, { rejectWithValue }) => {
    try {
      const response = await companyService.getCompanies(params);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch companies');
    }
  }
);

export const createCompany = createAsyncThunk(
  'company/createCompany',
  async (data: CompanyFormData, { rejectWithValue }) => {
    try {
      const response = await companyService.createCompany(data);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to create company');
    }
  }
);

export const updateCompany = createAsyncThunk(
  'company/updateCompany',
  async ({ id, data }: { id: string; data: CompanyFormData }, { rejectWithValue }) => {
    try {
      const response = await companyService.updateCompany(id, data);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to update company');
    }
  }
);

export const deleteCompany = createAsyncThunk(
  'company/deleteCompany',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await companyService.deleteCompany(id);
      return { ...response, id };
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to delete company');
    }
  }
);

export const fetchCompanyById = createAsyncThunk(
  'company/fetchCompanyById',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await companyService.getCompanyById(id);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch company');
    }
  }
);

export const companySlice = createSlice({
  name: 'company',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearCompany: (state) => {
      state.company = null;
    },
    setCompanies: (state, action: PayloadAction<Company[]>) => {
      state.companies = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Companies
      .addCase(fetchCompanies.pending, (state) => {
        state.companiesLoading = true;
        state.error = null;
      })
      .addCase(fetchCompanies.fulfilled, (state, action) => {
        state.companiesLoading = false;
        state.companies = action.payload.data.companies;
        state.pagination = action.payload.data.pagination;
        state.error = null;
      })
      .addCase(fetchCompanies.rejected, (state, action) => {
        state.companiesLoading = false;
        state.error = action.payload as string;
      })
      
      // Create Company
      .addCase(createCompany.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createCompany.fulfilled, (state, action) => {
        state.isLoading = false;
        state.companies.unshift(action.payload.data);
        state.error = null;
      })
      .addCase(createCompany.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      
      // Update Company
      .addCase(updateCompany.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateCompany.fulfilled, (state, action) => {
        state.isLoading = false;
        const updatedCompany = action.payload.data;
        const index = state.companies.findIndex(company => company._id === updatedCompany._id);
        if (index !== -1) {
          state.companies[index] = updatedCompany;
        }
        state.error = null;
      })
      .addCase(updateCompany.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      
      // Delete Company
      .addCase(deleteCompany.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteCompany.fulfilled, (state, action) => {
        state.isLoading = false;
        state.companies = state.companies.filter(company => company._id !== action.payload.id);
        state.error = null;
      })
      .addCase(deleteCompany.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      
      // Fetch Company By ID
      .addCase(fetchCompanyById.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchCompanyById.fulfilled, (state, action) => {
        state.isLoading = false;
        state.company = action.payload.data;
        state.error = null;
      })
      .addCase(fetchCompanyById.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearError, clearCompany, setCompanies } = companySlice.actions;
export default companySlice.reducer;